import { SdkContext } from '../internal'
import { listItems } from '@/lib/utils'
import type { Pagination } from '@/types/adminCommunities'
import type {
  CommunityRequest,
  CommunityRequestListFilters,
  CommunityRequestPlans
} from '@/types/adminCommunityRequests'

const cleanQuery = (params: Record<string, unknown>) =>
  Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''))

/**
 * F-89: requests to open a community, decided by the SuperAdmin. No
 * `X-CommunityId`: a request belongs to no community yet. Every route answers
 * 403 to anyone else.
 */
export class AdminCommunityRequestsResource {
  constructor(private ctx: SdkContext) {}

  /**
   * Pending ones oldest first; decided ones newest first. 20 per page by default.
   * Each item carries `communityType` (`COMMUNITY`, `COMPANY_HR`, `COWORKING` or null).
   */
  async list(filters: CommunityRequestListFilters = {}): Promise<{ items: CommunityRequest[]; pagination?: Pagination }> {
    const result = await this.ctx.raw<unknown>({ httpMethod: 'GET', path: '/admin/community-requests' }, { query: cleanQuery({ ...filters }) })
    return {
      items: listItems<CommunityRequest>(result),
      pagination: (result as { pagination?: Pagination } | null)?.pagination
    }
  }

  /** The plans of the main Memberfy community, each with its billing options. 503 when it does not exist here. */
  async plans(): Promise<CommunityRequestPlans> {
    return this.ctx.call({ httpMethod: 'GET', path: '/admin/community-requests/plans' }, {})
  }

  /** Same shape as a list item, `communityType` included. */
  async get(id: string): Promise<CommunityRequest> {
    return this.ctx.call({ httpMethod: 'GET', path: '/admin/community-requests/{id}' }, { id })
  }

  /** Creates the community with 90 free days on `productId`. 409 when already decided or the address got taken. */
  async approve(id: string, productId: string): Promise<CommunityRequest> {
    return this.ctx.call({ httpMethod: 'POST', path: '/admin/community-requests/{id}/approve', hasBody: true }, { id, body: { productId } })
  }

  /** The optional reason goes in the e-mail to the requester. 409 when already decided. */
  async reject(id: string, reason: string | null): Promise<CommunityRequest> {
    return this.ctx.call({ httpMethod: 'POST', path: '/admin/community-requests/{id}/reject', hasBody: true }, { id, body: { reason } })
  }

  /** Restarts the address setup of the created community. 409 when it is already active. */
  async retryDomain(id: string): Promise<CommunityRequest> {
    return this.ctx.call({ httpMethod: 'POST', path: '/admin/community-requests/{id}/domain/retry', hasBody: true }, { id, body: {} })
  }
}
